// Static region (state / province) data for the country-aware region
// field on the customer request form. Pure data + a tiny resolver — no
// Postgres / Redis / React deps — so BOTH surfaces import it: the
// hosted /request page AND the esbuild-bundled embed (single source of
// truth, no 4th inlined copy of geo data to drift).
//
// Scope (owner decision, see kickoffs/feedback-cascading-country-region):
// structured dropdowns for US + Canada only; every other country falls
// back to a free-text region input with a generic label. No address
// autocomplete, no external geo API, no new PCD sub-processor.
import { US_STATE_CODE_TO_NAME } from "@/lib/us-states";

export interface RegionOption {
  code: string;
  name: string;
}

export interface RegionConfig {
  /** "select" → render a dropdown of `options`; "text" → free-text input. */
  mode: "select" | "text";
  options?: RegionOption[];
  /** Locale-aware label for the region control (State / Province / …). */
  label: string;
}

// Curated short-list of common destinations, US + CA first, ending with
// an "Other" escape hatch. Deliberately NOT the full ISO set — expand
// only when a real customer needs a specific country (owner-flagged).
export const COUNTRIES: { code: string; name: string }[] = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "IE", name: "Ireland" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "MX", name: "Mexico" },
  { code: "OTHER", name: "Other" },
];

// US states / territories / military. Reuse the canonical map the
// Shopify draft-order PUT path already trusts (includes DC + territories
// + APO/FPO) so the hosted form, embed, and draft-order normalizer all
// agree on the valid code set — rather than the legacy bare 50-code array.
export const US_STATES: RegionOption[] = Object.entries(US_STATE_CODE_TO_NAME)
  .map(([code, name]) => ({ code, name }));

// Canadian provinces + territories (13). New data — no existing source.
export const CA_PROVINCES: RegionOption[] = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

/**
 * Single source for both the label localization and the
 * dropdown-vs-text decision, given a country code:
 *
 *   - "US" → { select, US_STATES,    "State" }
 *   - "CA" → { select, CA_PROVINCES, "Province" }
 *   - else → { text,   —,            "Region / State / Province" }
 *
 * Case-insensitive; nullish/unknown → free-text.
 */
export function regionConfigForCountry(code: string | null | undefined): RegionConfig {
  switch ((code || "").toUpperCase()) {
    case "US": return { mode: "select", options: US_STATES, label: "State" };
    case "CA": return { mode: "select", options: CA_PROVINCES, label: "Province" };
    default:   return { mode: "text", label: "Region / State / Province" };
  }
}

// Owner default: when a merchant hasn't set an allow-list, the request
// form offers United States only (single allowed country → no Country
// dropdown at all). Merchants open it up to more destinations via the
// Locations setting. (Deliberate US-first default — overrides the
// earlier "offer all by default" proposal.)
const DEFAULT_ALLOWED_COUNTRIES = ["US"];

/**
 * Resolve the offered country list from the merchant's allow-list.
 * Unknown codes are dropped; empty/unset → the US-only owner default.
 * Order follows COUNTRIES (stable, US-first) regardless of allow-list
 * order. Never strands the form with zero options — a wholly-unknown
 * allow-list falls back to the full COUNTRIES list.
 */
export function allowedCountries(allow: string[] | null | undefined): { code: string; name: string }[] {
  const list = (Array.isArray(allow) && allow.length > 0) ? allow : DEFAULT_ALLOWED_COUNTRIES;
  const set = new Set(list.map(c => c.toUpperCase()));
  const out = COUNTRIES.filter(c => set.has(c.code));
  return out.length ? out : COUNTRIES; // never strand the form with zero options
}

/**
 * Markets P2b AMEND 3 (kickoffs/market-quote-activation.md) — the rep
 * builder's ship-to / bill-to country pickers must NOT be gated by
 * `allowed_countries` (the request FORM's customer-facing funnel
 * filter — where can a customer pick, not where can a trusted rep say
 * an order ships). Independent axis from `allowedCountries()` above,
 * which stays exactly as-is for the storefront form + Locations
 * settings page. Excludes "OTHER": the rep's selection feeds the
 * presentment-draft `shippingAddress.countryCode` (Markets P2a) and
 * "OTHER" is not a valid Shopify CountryCode.
 */
export function repShipToCountries(): { code: string; name: string }[] {
  return COUNTRIES.filter(c => c.code !== "OTHER");
}

/**
 * Postal-field label for a country. US → "Zip code"; everything else →
 * "Postal code" (correct for CA / GB / AU / DE / …). Mirrors the
 * region-label localization. Kept separate from a merchant's
 * builtin_fields[zip].label override, which still wins when set.
 */
export function postalLabelForCountry(code: string | null | undefined): string {
  return (code || "").toUpperCase() === "US" ? "Zip code" : "Postal code";
}

interface PostalRule {
  pattern: RegExp;
  example: string;
  message: string;
}

// Postal-format rules, keyed by country code. Only add a pattern here when
// confident in the shape for that country — a wrong pattern hard-blocks the
// send button, which is worse than no check at all. Any country absent from
// this table (including blank/unknown) is deliberately permissive: see
// validatePostalForCountry below.
const POSTAL_RULES: Record<string, PostalRule> = {
  US: {
    pattern: /^\d{5}(-\d{4})?$/,
    example: "12345 or 12345-6789",
    message: "Ship-to ZIP must be 5 digits or ZIP+4 (e.g. 12345 or 12345-6789).",
  },
  CA: {
    // Letter-digit-letter [space|hyphen]? digit-letter-digit, case-insensitive.
    pattern: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
    example: "L6H 1M3",
    message: "Ship-to postal code doesn't look like a Canadian postal code (e.g. L6H 1M3).",
  },
};

export interface PostalValidationResult {
  valid: boolean;
  message?: string;
}

/**
 * Validate a postal/ZIP string against the ship-to country's rule.
 *
 * No rule for that country (unknown code, or blank/absent country) ⇒ always
 * valid — refusing to block beats guessing wrong; a bad postal surfaces at
 * Shopify checkout anyway, an unsendable quote is a hard stop. Blank/empty
 * postal is also always valid — callers decide whether postal is required.
 */
export function validatePostalForCountry(
  postal: string | null | undefined,
  country: string | null | undefined,
): PostalValidationResult {
  const trimmed = (postal || "").trim();
  if (!trimmed) return { valid: true };
  const rule = POSTAL_RULES[(country || "").toUpperCase()];
  if (!rule) return { valid: true };
  if (rule.pattern.test(trimmed)) return { valid: true };
  return { valid: false, message: rule.message };
}

const COUNTRY_CODE_MAP: Record<string, string> = Object.fromEntries(
  COUNTRIES.map(c => [c.code, c.name])
);

/**
 * Resolve a full country name from a country code. Falls back to the code
 * itself when not found (so unknown codes still round-trip rather than being
 * silently replaced with "United States").
 */
export function countryNameForCode(code: string | null | undefined): string {
  if (!code) return "";
  return COUNTRY_CODE_MAP[code.toUpperCase()] || code;
}
